import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, AlertTriangle, CreditCard, User, BadgeCheck } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { formatCOP } from '@/lib/format';
import { useSEO } from '@/lib/useSEO';

const ND = <span className="text-slate-400 italic">No disponible</span>;
const dt = (v) => (v ? new Date(v).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' }) : ND);
const ROLE = { buyer: 'Comprador', seller: 'Vendedor', admin: 'Administrador' };
const ACCOUNT = { active: 'Activa', pending: 'Pendiente', suspended: 'Suspendida', inactive: 'Inactiva' };
const PAY = { approved: ['Aprobado', 'bg-emerald-100 text-emerald-800'], cancelled: ['Cancelado', 'bg-slate-200 text-slate-700'], initiated: ['Iniciado', 'bg-amber-100 text-amber-800'] };
const SOURCE = { users: 'Cuenta', audit: 'Auditoría', payments: 'Pagos', account_events: 'Registro de eventos' };
const PLAN = { basic: 'Básico', pro: 'PRO' };
const GATEWAY = { simulated: 'Simulado', wompi: 'Wompi', launch_benefit: 'Beneficio de lanzamiento', manual: 'Manual' };
const periodLabel = (m) => (m ? `Cada ${m} ${m === 1 ? 'mes' : 'meses'}` : ND);

const Card = ({ title, icon: Icon, children, testid }) => (
  <section className="border border-slate-200 bg-white p-5" data-testid={testid}>
    <h2 className="font-display font-bold text-slate-900 text-sm uppercase tracking-wider flex items-center gap-2">
      {Icon && <Icon className="h-4 w-4 text-orange-600" />}{title}
    </h2>
    <dl className="mt-3 text-sm">{children}</dl>
  </section>
);
const F = ({ label, children, testid }) => (
  <div className="flex justify-between gap-4 py-1 border-b border-slate-100 last:border-0" data-testid={testid}>
    <dt className="text-slate-500">{label}</dt><dd className="text-right font-medium text-slate-900 break-all">{children ?? ND}</dd>
  </div>
);

const planState = (p) => {
  if (!p || p.never_paid) return 'Sin plan';
  if (p.expired) return 'Vencido';
  return 'Activo';
};

const SellerCards = ({ sp }) => {
  if (!sp) return null;
  const { activation: a, plan: p } = sp;
  return (
    <>
      <Card title="Activación" icon={BadgeCheck} testid="user-detail-activation">
        <F label="Estado">{a.never_paid ? 'No pagada' : 'Permanente'}</F>
        <F label="Fecha de activación">{dt(a.paid_at)}</F>
        <F label="Valor cobrado">{a.amount != null ? formatCOP(a.amount) : ND}</F>
        <F label="Periodicidad">Pago único</F>
        <F label="Vencimiento">No vence</F>
        <F label="Beneficio de lanzamiento">{a.launch_benefit ? 'Sí' : 'No'}</F>
      </Card>
      <Card title="Plan" icon={CreditCard} testid="user-detail-plan">
        <F label="Plan actual" testid="user-detail-plan-name">{PLAN[p.plan] || 'Sin plan'}</F>
        <F label="Estado del plan" testid="user-detail-plan-state">{planState(p)}</F>
        <F label="Contratación / último pago">{dt(p.paid_at)}</F>
        <F label="Inicio del período vigente">{dt(p.current_period_start)}</F>
        <F label="Vencimiento / próxima renovación" testid="user-detail-plan-exp">{dt(p.expires_at)}</F>
        <F label="Valor cobrado">{p.amount != null ? formatCOP(p.amount) : ND}</F>
        <F label="Periodicidad">{periodLabel(p.period_months)}</F>
        <F label="Próxima fecha de pago">{p.never_paid ? '—' : dt(p.expires_at)}</F>
      </Card>
    </>
  );
};

const Payments = ({ rows }) => (
  <section className="border border-slate-200 bg-white p-5" data-testid="user-detail-payments">
    <h2 className="font-display font-bold text-slate-900 text-sm uppercase tracking-wider">Pagos de plan y activación</h2>
    {!rows.length ? <p className="mt-3 text-sm text-slate-400 italic">Sin pagos registrados.</p> : (
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase text-slate-500"><tr>
            <th className="py-1">Iniciado</th><th>Concepto</th><th>Valor</th><th>Estado</th><th>Aprobado</th><th>Medio</th></tr></thead>
          <tbody>{rows.map((p) => (
            <tr key={p.payment_id} className="border-t border-slate-100" data-testid={`user-detail-payment-${p.payment_id}`}>
              <td className="py-1.5">{dt(p.created_at)}</td>
              <td>{p.type === 'plan' ? `Plan ${PLAN[p.plan] || p.plan || ''}` : p.is_renewal ? 'Renovación activación' : 'Activación'}</td>
              <td>{formatCOP(p.amount)}</td>
              <td><Badge className={`rounded-none ${PAY[p.state]?.[1] || ''}`}>{PAY[p.state]?.[0] || p.state}</Badge></td>
              <td>{p.approved_at ? dt(p.approved_at) : ND}</td>
              <td>{GATEWAY[p.gateway] || p.gateway || ND}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    )}
  </section>
);

const Timeline = ({ d }) => (
  <section className="border border-slate-200 bg-white p-5" data-testid="user-detail-timeline">
    <h2 className="font-display font-bold text-slate-900 text-sm uppercase tracking-wider flex items-center gap-2"><Clock className="h-4 w-4 text-orange-600" />Historial cronológico</h2>
    <ol className="relative border-l border-slate-200 ml-2 mt-3">
      {d.timeline.map((t, i) => (
        <li key={`${t.at}-${i}`} className="mb-4 ml-4" data-testid={`user-timeline-item-${i}`}>
          <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border border-white bg-orange-500" />
          <div className="text-xs text-slate-500">{dt(t.at)} · {SOURCE[t.source] || t.source}</div>
          <div className="font-semibold text-slate-900">{t.description}</div>
          <div className="text-xs text-slate-500">Responsable: {t.actor}</div>
        </li>
      ))}
    </ol>
    {!!d.missing.length && (
      <div className="mt-2 border-l-4 border-amber-400 bg-amber-50 p-3 text-xs text-amber-900" data-testid="user-detail-missing">
        <AlertTriangle className="inline h-3.5 w-3.5 mr-1" />No disponible: {d.missing.join(' · ')}.
      </div>
    )}
  </section>
);

export default function AdminUserDetail() {
  const { userId } = useParams();
  useSEO({ title: 'Detalle de usuario | Admin' });
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    api.get(`/admin/users/${userId}/full`).then(({ data }) => setD(data))
      .catch((e) => setErr(e.response?.data?.detail || 'No se pudo cargar el usuario'));
  }, [userId]);
  const u = d?.user;
  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="admin-user-detail-page">
        <Link to="/admin/usuarios" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-orange-600" data-testid="user-detail-back-link">
          <ArrowLeft className="h-4 w-4" /> Usuarios
        </Link>
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600 mt-3">Administración</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1 break-all">{u?.name || userId}</h1>
        {err && <p className="mt-6 text-red-700" data-testid="user-detail-error">{err}</p>}
        {!d && !err && <p className="mt-6 text-slate-500">Cargando…</p>}
        {d && (
          <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
            <div className="space-y-5 min-w-0">
              <Card title="Información general" icon={User} testid="user-detail-general">
                <F label="Nombre">{u.name}</F>
                <F label="Correo">{u.email}</F>
                <F label="Teléfono">{u.phone || null}</F>
                <F label="Tipo de usuario">{ROLE[u.role] || u.role}</F>
                <F label="Fecha de registro">{dt(u.created_at)}</F>
                <F label="Estado de la cuenta" testid="user-detail-account-state">{ACCOUNT[d.account_state] || d.account_state}</F>
                {u.role === 'seller' && <F label="Estado del vendedor">{d.seller_profile ? (d.seller_profile.seller_status === 'approved' ? 'Aprobado' : 'Pendiente de aprobación') : null}</F>}
              </Card>
              <SellerCards sp={d.seller_profile} />
              {u.role === 'seller' && !d.seller_profile && (
                <p className="border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="user-detail-no-profile">
                  Perfil de vendedor incompleto: plan y activación No disponible.
                </p>
              )}
            </div>
            <div className="space-y-5 min-w-0"><Timeline d={d} /><Payments rows={d.payments} /></div>
          </div>
        )}
      </div>
    </Layout>
  );
}
